// URL Parameters Utility
class UrlParamsService {
  constructor() {
    this.PARTNER_ID_KEY = '_partner_id_';
    this.listeners = [];
    this.initUrlChangeListener();
  }

  /**
   * Get URL parameter value by name
   * @param {string} paramName - Parameter name to search for
   * @returns {string|null} - Parameter value or null if not found
   */
  getUrlParam(paramName) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(paramName);
  }

  /**
   * Get ref parameter from URL and store as partner_id
   * @returns {number|null} - Partner ID from ref parameter or stored value
   */
  getPartnerId() {
    // First check if we already have a stored partner_id
    const storedPartnerId = this.getStoredPartnerId();
    
    // Check URL for ref parameter
    const refValue = this.getUrlParam('ref');
    if (refValue) {
      const partnerId = parseInt(refValue);
      if (!isNaN(partnerId) && partnerId > 0) {
        // Store the partner_id for the entire session
        this.setStoredPartnerId(partnerId);
        return partnerId;
      }
    }

    // If no ref parameter, return stored partner_id or default to 1
    return storedPartnerId || 1;
  }

  /**
   * Store partner_id in session storage
   * @param {number} partnerId - Partner ID to store
   */
  setStoredPartnerId(partnerId) {
    try {
      sessionStorage.setItem(this.PARTNER_ID_KEY, partnerId.toString());
    } catch (error) {
      console.warn('Failed to store partner_id in session storage:', error);
    }
  }

  /**
   * Get stored partner_id from session storage
   * @returns {number|null} - Stored partner ID or null
   */
  getStoredPartnerId() {
    try {
      const stored = sessionStorage.getItem(this.PARTNER_ID_KEY);
      return stored ? parseInt(stored) : null;
    } catch (error) {
      console.warn('Failed to get partner_id from session storage:', error);
      return null;
    }
  }

  /**
   * Clear stored partner_id from session storage
   */
  clearStoredPartnerId() {
    try {
      sessionStorage.removeItem(this.PARTNER_ID_KEY);
    } catch (error) {
      console.warn('Failed to clear partner_id from session storage:', error);
    }
  }

  /**
   * Check if current URL has ref parameter
   * @returns {boolean} - True if ref parameter exists
   */
  hasRefParameter() {
    return this.getUrlParam('ref') !== null;
  }

  /**
   * Get all URL parameters as object
   * @returns {Object} - Object containing all URL parameters
   */
  getAllParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const params = {};
    for (const [key, value] of urlParams) {
      params[key] = value;
    }
    return params;
  }

  /**
   * Initialize URL change listener for dynamic ref parameter updates
   */
  initUrlChangeListener() {
    // Listen for browser navigation events
    window.addEventListener('popstate', () => {
      this.handleUrlChange();
    });

    // Listen for programmatic navigation (React Router)
    window.addEventListener('pushstate', () => {
      this.handleUrlChange();
    });

    // Override history.pushState to catch programmatic navigation
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function(...args) {
      originalPushState.apply(window.history, args);
      window.dispatchEvent(new Event('pushstate'));
    };

    window.history.replaceState = function(...args) {
      originalReplaceState.apply(window.history, args);
      window.dispatchEvent(new Event('pushstate'));
    };
  }

  /**
   * Handle URL change and update partner_id if ref parameter changed
   */
  handleUrlChange() {
    const newRefValue = this.getUrlParam('ref');
    const currentPartnerId = this.getStoredPartnerId();
    
    if (newRefValue) {
      const newPartnerId = parseInt(newRefValue);
      if (!isNaN(newPartnerId) && newPartnerId > 0 && newPartnerId !== currentPartnerId) {
        // Update stored partner_id only when ref parameter changes
        this.setStoredPartnerId(newPartnerId);
        // Notify listeners
        this.notifyListeners(newPartnerId);
      }
    }
    // If no ref parameter, do not update partner_id - keep existing value
  }

  /**
   * Add listener for partner_id changes
   * @param {Function} callback - Callback function to call when partner_id changes
   */
  addPartnerIdChangeListener(callback) {
    this.listeners.push(callback);
  }

  /**
   * Remove listener for partner_id changes
   * @param {Function} callback - Callback function to remove
   */
  removePartnerIdChangeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  /**
   * Notify all listeners about partner_id change
   * @param {number} newPartnerId - New partner ID value
   */
  notifyListeners(newPartnerId) {
    this.listeners.forEach(callback => {
      try {
        callback(newPartnerId);
      } catch (error) {
        console.warn('Error in partner_id change listener:', error);
      }
    });
  }

  /**
   * Force refresh partner_id from current URL
   * @returns {number} - Updated partner ID
   */
  refreshPartnerId() {
    this.handleUrlChange();
    return this.getPartnerId();
  }
}

// Create and export singleton instance
const urlParamsService = new UrlParamsService();
export default urlParamsService;
